import { PayloadAction, AnyAction } from "@reduxjs/toolkit";
import { ReduxHelper } from "helpers";
import { IBaseReducerState } from "helpers/reduxHelpers";
import {
  ICompany,
  ILocation,
  IPaginateApiResponse,
  IPallet,
  IProduct,
  IProductCategory,
  IUnitMeasure,
  IUser,
  IWarehouse,
  IZone,
  IZoneTemperature,
} from "model";

import { MasterDataActions } from "redux/actions";

interface IBaseMasterData<T, U = any> {
  data?: T;
  current?: U;
  action: string;
}
interface IMasterDataState extends IBaseReducerState {
  warehouse: IBaseMasterData<IPaginateApiResponse<IWarehouse[]>, IWarehouse>;
  product: IBaseMasterData<IPaginateApiResponse<IProduct[]>, IProduct>;
  zone: IBaseMasterData<IPaginateApiResponse<IZone[]>, IZone>;
  pallet: IBaseMasterData<IPaginateApiResponse<IPallet[]>>;
  location: IBaseMasterData<IPaginateApiResponse<ILocation[]>>;
  company: IBaseMasterData<IPaginateApiResponse<ICompany[]>, ICompany>;
  user: IBaseMasterData<IPaginateApiResponse<IUser[]>, IUser>;
  productCategory: IBaseMasterData<IProductCategory[]>;
  unitMeasure: IBaseMasterData<IUnitMeasure[]>;
  zoneTemperature: IBaseMasterData<IZoneTemperature[]>;
}

const initialState: IMasterDataState = {
  action: MasterDataActions.prefix,
  prefix: MasterDataActions.prefix,
  warehouse: {
    action: "WAREHOUSE",
  },
  product: {
    action: "PRODUCT",
  },
  pallet: {
    action: "PALLET",
  },
  zone: {
    action: "ZONE",
  },
  location: {
    action: "LOCATION",
  },
  company: {
    action: "COMPANY",
  },
  user: {
    action: "USER",
  },
  productCategory: {
    action: "PRODUCT_CATEGORIES",
  },
  unitMeasure: {
    action: "UNIT_MEASURE",
  },
  zoneTemperature: {
    action: "ZONE_TEMPERATURE",
  },
};

const getProductCategoriesSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IProductCategory[]>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.productCategory.data = action.payload;
    state.productCategory.action = action.type;
  });

const getUnitMeasureSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IUnitMeasure[]>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.unitMeasure.data = action.payload;
    state.unitMeasure.action = action.type;
  });
const getZoneTemperatureSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IZoneTemperature[]>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.zoneTemperature.data = action.payload;
    state.zoneTemperature.action = action.type;
  });

const getAllProductsSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<IProduct[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.product.data = action.payload;
    state.product.action = action.type;
  });
const getAllPalletsSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<IPallet[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.pallet.data = action.payload;
    state.pallet.action = action.type;
  });
const getAllWarehousesSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<IWarehouse[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.warehouse.data = action.payload;
    state.warehouse.action = action.type;
  });
const getAllZonesSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<IZone[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.zone.data = action.payload;
    state.zone.action = action.type;
  });
const getAllLocationSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<ILocation[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.location.data = action.payload;
    state.location.action = action.type;
  });
const getAllCompaniesSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<ICompany[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.company.data = action.payload;
    state.company.action = action.type;
  });

const getAllUserSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IPaginateApiResponse<IUser[]>>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.user.data = action.payload;
    state.user.action = action.type;
  });

const getProductByIdSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IProduct>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.product.current = action.payload;
    state.product.action = action.type;
  });
const getWarehouseByIdSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IWarehouse>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.warehouse.current = action.payload;
    state.warehouse.action = action.type;
  });
const getCompanyByIdSuccess = (
  state: IMasterDataState,
  action: PayloadAction<ICompany>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.company.current = action.payload;
    state.company.action = action.type;
  });
const getUserByIdSuccess = (
  state: IMasterDataState,
  action: PayloadAction<IUser>
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.user.current = action.payload;
    state.user.action = action.type;
  });

const clearCurrentProduct = (state: IMasterDataState, action: PayloadAction) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.product.current = undefined;
    state.product.action = action.type;
  });
const clearCurrentWarehouse = (
  state: IMasterDataState,
  action: PayloadAction
) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.warehouse.current = undefined;
    state.warehouse.action = action.type;
  });
const clearCurrentCompany = (state: IMasterDataState, action: PayloadAction) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.company.current = undefined;
    state.company.action = action.type;
  });
const clearCurrentUser = (state: IMasterDataState, action: PayloadAction) =>
  ReduxHelper.handleReducerBuilder(state, action, () => {
    state.user.current = undefined;
    state.user.action = action.type;
  });

const MasterDataReducer = ReduxHelper.createHandleReducer<IMasterDataState>(
  initialState,
  (builder) => {
    builder
      .addCase(
        MasterDataActions.getProductCategories.success,
        getProductCategoriesSuccess
      )
      .addCase(MasterDataActions.getUnitMeasure.success, getUnitMeasureSuccess)
      .addCase(
        MasterDataActions.getZoneTemperature.success,
        getZoneTemperatureSuccess
      )
      .addCase(
        MasterDataActions.getAllWarehouses.success,
        getAllWarehousesSuccess
      )
      .addCase(MasterDataActions.getAllZones.success, getAllZonesSuccess)
      .addCase(MasterDataActions.getAllLocations.success, getAllLocationSuccess)
      .addCase(MasterDataActions.getAllProducts.success, getAllProductsSuccess)
      .addCase(MasterDataActions.getAllPallets.success, getAllPalletsSuccess)
      .addCase(
        MasterDataActions.getAllCompanies.success,
        getAllCompaniesSuccess
      )
      .addCase(MasterDataActions.getAllUsers.success, getAllUserSuccess)
      .addCase(
        MasterDataActions.getWarehouseById.success,
        getWarehouseByIdSuccess
      )
      .addCase(MasterDataActions.getProductById.success, getProductByIdSuccess)
      .addCase(MasterDataActions.getUserById.success, getUserByIdSuccess)
      .addCase(MasterDataActions.getCompanyById.success, getCompanyByIdSuccess)
      .addCase(
        MasterDataActions.clearCurrentWarehouse.request,
        clearCurrentWarehouse
      )
      .addCase(
        MasterDataActions.clearCurrentProduct.request,
        clearCurrentProduct
      )
      .addCase(
        MasterDataActions.clearCurrentCompany.request,
        clearCurrentCompany
      )
      .addCase(MasterDataActions.clearCurrentUser.request, clearCurrentUser);
  },
  (state: IMasterDataState, action: AnyAction) => {
    const watcherAction = action.type;
    if (watcherAction?.includes("ZONE_TEMPERATURE")) {
      state.zoneTemperature.action = action.type;
    }
    if (watcherAction?.includes("PRODUCT_CATEGORIES")) {
      state.productCategory.action = action.type;
    }
    if (watcherAction?.includes("UNIT_MEASURE")) {
      state.unitMeasure.action = action.type;
    }
    if (watcherAction?.includes("WAREHOUSE")) {
      state.warehouse.action = action.type;
    }
    if (watcherAction?.includes("PALLET")) {
      state.pallet.action = action.type;
    }
    if (
      watcherAction?.includes("PRODUCT") &&
      !watcherAction.includes("CATEGORIES")
    ) {
      state.product.action = action.type;
    }
    if (
      watcherAction?.includes("ZONE") &&
      !watcherAction.includes("TEMPERATURE")
    ) {
      state.zone.action = action.type;
    }
    if (watcherAction?.includes("LOCATION")) {
      state.location.action = action.type;
    }
    if (
      watcherAction?.includes("COMPANY") ||
      watcherAction?.includes("COMPANIES")
    ) {
      state.company.action = action.type;
    }
    if (watcherAction?.includes("USER")) {
      state.user.action = action.type;
    }
  }
);

export default MasterDataReducer;
